import { AppBar, Box, Stack, Typography, styled } from '@mui/material'
import Image from 'next/image'
import { ReactNode } from 'react'
import { useAuthenticatedUser, useIsSupplier } from '@omnea-app/fe-auth/UserSessionProvider'
import { useIsMobileWidth } from '@omnea-app/fe-browser-utils/useDetectDevice'
import { MobileMenu } from './MobileMenu'
import { UserDetails } from './UserDetails'

interface Props {
  title?: string | ReactNode
  children?: ReactNode
  showLogo?: boolean
  variant?: 'dark' | 'light'
}

export const CustomAppBar = styled(AppBar)(({ theme }) => ({
  border: 0,
  borderBottom: '1px solid',
  borderBottomColor: 'rgba(0,0,0,0.12)',
  borderRadius: '0 !important',
  boxShadow: 'none',
  paddingLeft: 44,
  paddingRight: 0,
  paddingTop: 8,
  paddingBottom: 8,
  height: theme.mixins.toolbar.height,
  justifyContent: 'center',
}))

export const Header = ({ title, children, showLogo = false, variant = 'light' }: Props) => {
  const userData = useAuthenticatedUser()
  const isSupplier = useIsSupplier()
  const isMobileWidth = useIsMobileWidth()

  if (!userData.isFullyAuthenticated) {
    return null
  }

  return (
    <CustomAppBar
      position="static"
      sx={{
        borderRadius: 0,
        background: variant === 'dark' ? '#272833' : '#F1F2F5',
        borderBottomColor: variant === 'dark' ? '#373737' : 'rgba(0,0,0,0.12)',
        padding: 0,
        paddingX: { xs: 2, md: 0 },
        paddingLeft: { md: '30px' },
        backgroundImage: userData.org.isQaOrg ? "url('/app/assets/qa-pattern.svg')" : undefined,
      }}
    >
      <Stack alignItems="center" justifyContent="space-between">
        <Stack direction="row" sx={{ width: '100%' }} alignItems="center" spacing={{ xs: 2, md: 6 }}>
          {isMobileWidth && !isSupplier ? <MobileMenu /> : null}
          {showLogo && (
            <Box alignContent="center">
              {variant === 'light' ? (
                <Image src="/logo-light.png" alt="" height={24} width={87} />
              ) : (
                <Image src="/logo.svg" alt="Omnea logo" height={24} width={123} />
              )}
            </Box>
          )}
          <Box overflow="hidden">
            <Typography variant="Body16pxHeavy" color={variant === 'light' ? 'header.text' : '#FFF'}>
              {title}
            </Typography>
          </Box>
          <Spacer />
          <UserDetails variant={variant} view={isMobileWidth ? 'minimal' : 'full'} />
        </Stack>
        {children}
      </Stack>
    </CustomAppBar>
  )
}

const Spacer = () => <Box flexGrow={1} />
